/**
 * SUPPORT-ONLY. Not exposed to clients.
 *
 * Fetches a public ChargeLab receipt page and scrapes the embedded
 * `receiptData` object. Receipts are unauthenticated HTML at
 * `${webAppUrlPrefix}/receipts/<id>` where webAppUrlPrefix is the
 * deployment's webapp URL with any color suffix stripped (see
 * getReceiptWebAppUrlPrefix). This is cross-origin from the dashboard —
 * CORS errors are surfaced as ReceiptCorsError so the caller can degrade
 * gracefully (load the OCPP logs without the billing overlay).
 */
import {
  extractJsonAfter,
  parseReceiptData,
  type RawReceiptData,
  type ReceiptInfo,
} from './receiptParser';

export class ReceiptCorsError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ReceiptCorsError';
  }
}

/** Fetch + parse a receipt page into the device id, session window, and summary. */
export async function fetchReceipt(
  receiptId: string,
  webAppUrlPrefix: string,
): Promise<ReceiptInfo> {
  const id = receiptId.trim();
  if (!id) throw new Error('enter a receipt id');

  const base = (webAppUrlPrefix || 'https://charge.id').replace(/\/+$/, '');
  const url = `${base}/receipts/${encodeURIComponent(id)}`;

  let res: Response;
  try {
    res = await fetch(url, { headers: { accept: 'text/html' } });
  } catch (e) {
    // A network/CORS failure rejects rather than returning a response.
    throw new ReceiptCorsError(
      `couldn't reach the receipt page at ${url} — charge.id may not allow cross-origin requests from this dashboard (${
        e instanceof Error ? e.message : String(e)
      })`,
    );
  }
  if (!res.ok) throw new Error(`receipt fetch ${res.status} ${res.statusText} for "${id}"`);

  const html = await res.text();

  // An unknown id still returns the SPA shell (HTTP 200) — it just has no
  // embedded receiptData. Translate that into a readable "bad id" error.
  let data: RawReceiptData;
  try {
    data = JSON.parse(extractJsonAfter(html, 'receiptData = ')) as RawReceiptData;
  } catch {
    throw new Error(
      `"${id}" doesn't look like a valid receipt id — ${base}/receipts/${id} has no embedded receipt data (expected an id like "s-…" from a receipt URL)`,
    );
  }

  return parseReceiptData(id, data);
}
